import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const LeadMagnetSection = ({ onDownload }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDownload = async (e) => {
    e?.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    onDownload({ email, resource: 'ai-survival-blueprint' });
    setIsSubmitting(false);
  };

  const blueprintFeatures = [
    {
      icon: 'Target',
      title: 'Strategic Framework',
      description: 'Complete methodology for AI transformation planning and execution'
    },
    {
      icon: 'Shield',
      title: 'Risk Mitigation',
      description: 'Comprehensive vendor lock-in prevention and security strategies'
    },
    {
      icon: 'TrendingUp',
      title: 'ROI Calculator',
      description: 'Interactive tools to calculate open-source AI implementation ROI'
    },
    {
      icon: 'Users',
      title: 'Team Readiness',
      description: 'Assessment frameworks for organizational AI capability building'
    }
  ];

  const testimonials = [
    {
      quote: `The AI Survival Blueprint gave us the confidence to move away from expensive proprietary solutions. We've saved over $2M annually while gaining complete control over our AI infrastructure.`,
      author: 'Sarah Chen',role: 'CTO, TechFlow Industries',avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=64&h=64&fit=crop&crop=face'
    },
    {
      quote: `This blueprint transformed our approach to AI strategy. The vendor independence framework alone was worth the entire consultation investment.`,
      author: 'Michael Rodriguez',role: 'VP Engineering, DataCore Systems',avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=64&h=64&fit=crop&crop=face'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-brand-primary to-blue-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div>
            <div className="inline-flex items-center px-4 py-2 bg-brand-accent/20 rounded-full text-sm font-medium mb-6">
              <Icon name="Download" size={16} className="mr-2 text-brand-accent" />
              Free Strategic Resource
            </div>

            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Download the Complete
              <span className="block text-brand-accent">AI Survival Blueprint</span>
            </h2>

            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Get immediate access to our comprehensive 47-page guide that has helped over 500 organizations break free from vendor lock-in and build sustainable AI capabilities.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {blueprintFeatures?.map((feature, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-brand-accent/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Icon name={feature?.icon} size={16} className="text-brand-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">{feature?.title}</h4>
                    <p className="text-sm text-blue-200">{feature?.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Download Form */}
            <form onSubmit={handleDownload} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Input
                    type="email"
                    placeholder="Enter your business email"
                    value={email}
                    onChange={(e) => setEmail(e?.target?.value)}
                    required
                    className="bg-white text-gray-900"
                  />
                </div>
                <Button
                  type="submit"
                  variant="default"
                  loading={isSubmitting}
                  disabled={!email || isSubmitting}
                  className="bg-brand-accent hover:bg-brand-accent/90 text-gray-900 font-semibold px-8"
                  iconName="Download"
                  iconPosition="left"
                >
                  {isSubmitting ? 'Sending...' : 'Get Blueprint'}
                </Button>
              </div>
              <p className="text-xs text-blue-200 mt-2">
                No spam. Unsubscribe anytime. Your email is safe with us.
              </p>
            </form>

            <div className="flex items-center space-x-6 text-sm">
              <div className="flex items-center">
                <Icon name="Users" size={16} className="mr-2 text-brand-accent" />
                <span>500+ Downloads</span>
              </div>
              <div className="flex items-center">
                <Icon name="Star" size={16} className="mr-2 text-yellow-400" />
                <span>4.9/5 Rating</span>
              </div>
              <div className="flex items-center">
                <Icon name="FileText" size={16} className="mr-2 text-green-400" />
                <span>47 Pages</span>
              </div>
            </div>
          </div>

          {/* Visual */}
          <div className="relative">
            <div className="relative z-10">
              <Image
                src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=600&h=400&fit=crop"
                alt="AI Strategy Blueprint Preview"
                className="rounded-2xl shadow-executive-lg"
              />
              
              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 bg-brand-accent text-gray-900 px-4 py-2 rounded-lg font-semibold text-sm">
                FREE Download
              </div>
              
              <div className="absolute -bottom-4 -left-4 bg-white text-brand-primary px-4 py-2 rounded-lg shadow-lg">
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={16} className="text-green-500" />
                  <span className="text-sm font-medium">Instant Access</span>
                </div>
              </div>
            </div>

            {/* Background Elements */}
            <div className="absolute inset-0 bg-gradient-to-br from-brand-accent/20 to-transparent rounded-2xl transform rotate-3"></div>
            <div className="absolute inset-0 bg-gradient-to-tl from-white/10 to-transparent rounded-2xl transform -rotate-2"></div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="mt-20">
          <h3 className="text-2xl font-bold text-center mb-12">
            What Leaders Are Saying About Our Blueprint
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials?.map((testimonial, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <div className="flex items-start space-x-4">
                  <Image
                    src={testimonial?.avatar}
                    alt={testimonial?.author}
                    className="w-12 h-12 rounded-full"
                  />
                  <div className="flex-1">
                    <p className="text-blue-100 mb-4 italic">"{testimonial?.quote}"</p>
                    <div>
                      <div className="font-semibold">{testimonial?.author}</div>
                      <div className="text-sm text-blue-200">{testimonial?.role}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadMagnetSection;